// 관리자 화면 틀(시안 v6). 신원은 직원 토큰(v6common/auth.js) — 없으면 로그인 화면, 있으면 user.menus 로 사이드바를 그린다.
// 메뉴 열쇠(view)는 예전 값을 그대로 쓰고(auth.MENU_VIEW 로 변환), 화면은 v6 페이지로 바뀌었다.
// 예전 화면 파일(admin_components/*.js)은 되돌리기용으로 남겨 두었다.
import { useEffect, useState } from 'react';
import Sidebar, { menuLabel } from "../admin_components/v6common/Sidebar";
import LoginScreen from "../admin_components/v6common/LoginScreen";
import { getMe, getToken, saveMe, clearAuth, allowedViews, AUTH_EVENT } from "../admin_components/v6common/auth";
import * as authApi from "../admin_components/v6common/authApi";
import { getSummary } from "../admin_components/inquiry/v6Api";
import Inquiry from '../admin_components/inquiry/InquiryPage';
import Dashboard from '../admin_components/v6pages/dashboard/Page';
import Notice from '../admin_components/v6pages/notice/Page';
import Knowledge from '../admin_components/v6pages/knowledge/Page';
import Model from '../admin_components/v6pages/model/Page';
import Report from '../admin_components/v6pages/report/Page';
import Ops from '../admin_components/v6pages/ops/Page';
import Chat from '../admin_components/v6pages/chat/Page';

import "../admin_styles/legacy_global.css"; // 가맹점주 화면도 기대던 전역 규칙(예전 관리자 CSS에서 옮김)
import "../admin_styles/inquiry_renewal.css";
import "../admin_styles/admin_v6.css";
import "../admin_styles/shell_v6.css";
import "../admin_styles/login_v6.css";

const PAGES = { dashboard: Dashboard, notice: Notice, knowledge: Knowledge, assistant: Model, chart: Report, chatbot: Ops, chat_history: Chat };
const SUMMARY_EVERY_MS = 30000;
const FOLD_KEY = "admin_sb_min";

const loadFold = () => { try { return localStorage.getItem(FOLD_KEY) === "1"; } catch { return false; } };
const saveFold = (v) => { try { localStorage.setItem(FOLD_KEY, v ? "1" : "0"); } catch { /* 저장 못 해도 화면은 동작 */ } };
// 메일의 재설정 링크 `{관리자 웹}/#reset?token=…`(계약 §2)
// 신원이 끊긴 이유 → 로그인 화면 안내(계약 §1)
const AUTH_NOTICE = {
    disabled: "사용 중지된 계정입니다. 대표관리자에게 문의하세요.",
    login: "로그인이 만료되었습니다. 다시 로그인해 주세요.",
};
const resetTokenFromHash = () => {
    const h = window.location.hash || "";
    if (!h.startsWith("#reset")) return null;
    return new URLSearchParams(h.slice(h.indexOf("?") + 1)).get("token") || null;
};

export default function Adminpage() {
    const [me, setMe] = useState(() => (getToken() ? getMe() : null));
    const [resetToken, setResetToken] = useState(resetTokenFromHash);
    const [notice, setNotice] = useState("");
    // 첫 로그인 비밀번호 변경 전이면 틀 대신 변경 화면(그 상태의 토큰은 /me·/password 만 된다)
    const [mustChange, setMustChange] = useState(() => !!getMe()?.must_change_password);
    const [view, setView] = useState(sessionStorage.getItem('admin_view') || 'inquiry');
    const [params, setParams] = useState(null);
    const [folded, setFolded] = useState(loadFold);
    const [mOpen, setMOpen] = useState(false);
    const [unread, setUnread] = useState(0);

    const views = allowedViews(me);
    const current = views.includes(view) ? view : (views[0] || "inquiry");
    useEffect(() => {
        if (me && current !== view) { setView(current); sessionStorage.setItem('admin_view', current); }
    }, [me, current, view]);

    // 토큰이 죽으면(만료·사용 중지) v6Api 가 이 이벤트를 쏜다 → 로그인 화면으로
    useEffect(() => {
        const onAuth = (e) => {
            const reason = e.detail?.reason;
            if (reason === "password") { setMustChange(true); return; }
            setNotice(AUTH_NOTICE[reason] || "");
            setMustChange(false);
            setMe(null);
        };
        window.addEventListener(AUTH_EVENT, onAuth);
        return () => window.removeEventListener(AUTH_EVENT, onAuth);
    }, []);

    // 시작할 때 내 정보를 새로 받는다(권한·이름이 바뀌었을 수 있다). 실패해도 저장값으로 그린다 — 401 이면 위 이벤트가 처리
    useEffect(() => {
        if (!me) return undefined;
        let alive = true;
        authApi.me().then((fresh) => { if (alive && fresh) { saveMe(fresh); setMe(fresh); if (fresh.must_change_password) setMustChange(true); } }).catch(() => {});
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [me?.id]);

    // 사이드바 「문의 관리」 배지: 로그인한 뒤, 30초마다
    useEffect(() => {
        if (!me || mustChange) return undefined;
        let alive = true;
        const load = () => getSummary().then((s) => alive && setUnread(s?.unread || 0)).catch(() => {});
        load();
        const timer = setInterval(load, SUMMARY_EVERY_MS);
        return () => { alive = false; clearInterval(timer); };
    }, [me, mustChange]);

    const go = (next, nextParams = null) => {
        setView(next);
        setParams(nextParams);
        sessionStorage.setItem('admin_view', next);
    };
    const toggleFold = () => setFolded((v) => { saveFold(!v); return !v; });
    const logout = () => { clearAuth(); setMe(null); setUnread(0); setNotice(""); setMustChange(false); };
    const onLogin = (user) => {
        setNotice("");
        setMustChange(false);
        setMe(user);
        if (resetToken) { setResetToken(null); window.location.hash = ""; }
    };

    if (!me || mustChange) {
        return (
            <main className="inq-r">
                <LoginScreen key={mustChange ? "change" : resetToken || "login"} onDone={onLogin} resetToken={mustChange ? null : resetToken}
                    initialStep={mustChange ? "change" : null} notice={notice} />
            </main>
        );
    }

    const Page = PAGES[current];
    return (
        <div className={`v6shell ${folded ? "sb-min" : ""}`}>
            <Sidebar view={current} views={views} me={me} onMenu={(k) => go(k)}
                unread={unread} onFold={toggleFold} open={mOpen} onClose={() => setMOpen(false)} onLogout={logout} />
            <header className="v6mtop">
                <button className="ham" aria-label="메뉴" onClick={() => setMOpen(true)}><i className="fa-solid fa-bars"></i></button>
                <b>{menuLabel(current)}</b>
                {unread > 0 && <span className="badge" title="안 읽은 문의">{unread}</span>}
            </header>
            {current === "inquiry"
                ? <Inquiry key={`inquiry-${JSON.stringify(params)}`} me={me} params={params} go={go} />
                : Page ? <Page key={`${current}-${JSON.stringify(params)}`} go={go} params={params} me={me} /> : <div>준비 중입니다: {current}</div>}
        </div>
    );
}
